/**
 * Renders an HTML mail body as readable plain text.
 *
 * A vendor replying from HoneyBook, Dubsado, or Apple Mail rich text sends a
 * `text/html` part and nothing else. The thread view reads only the plain-text
 * part, so those replies arrived as an empty message box — indistinguishable,
 * to the planner, from a vendor who sent an empty email. That is a large share
 * of wedding-vendor correspondence, not an edge case.
 *
 * Quoted history comes back as `> `-prefixed lines rather than as a second
 * return value, because `splitQuotedReply` already cuts on a quoted line. That
 * is the point of the design: HTML mail gets the same collapse toggle as
 * plain-text mail, with no second quote detector to keep in step with the
 * first. The ingest path's `stripHtml` deletes quotes outright and flattens
 * everything onto one line — right for keyword matching, useless for reading.
 *
 * This is string work, not parsing. Anything that would need a real parser to
 * get right — tables as grids, `<pre>` fidelity, quote depth past one level —
 * is deliberately out of scope; the text has to be readable, not faithful.
 */

/** Quote depth markers, carried through the transforms below as characters no
 *  mail body contains. Entity decoding refuses to emit control characters, so
 *  a sender cannot forge one and re-level their own quotes. */
const QUOTE_OPEN = '\u0000';
const QUOTE_CLOSE = '\u0001';

const NAMED_ENTITIES: Record<string, string> = {
  nbsp: ' ',
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
};

function decodeEntities(text: string): string {
  return text.replace(/&(#[0-9]+|#x[0-9a-f]+|[a-z][a-z0-9]*);/gi, (match, entity: string) => {
    if (entity[0] !== '#') return NAMED_ENTITIES[entity.toLowerCase()] ?? match;

    const code =
      entity[1] === 'x' || entity[1] === 'X'
        ? parseInt(entity.slice(2), 16)
        : Number(entity.slice(1));
    // A control character would collide with the quote markers above, and no
    // legitimate body needs one.
    if (!Number.isFinite(code) || code < 32 || code > 0x10ffff) return '';
    return String.fromCodePoint(code);
  });
}

/**
 * Turns the quote markers into line prefixes.
 *
 * Depth is tracked rather than assumed because Gmail nests a `<blockquote>`
 * inside its `gmail_quote` container, whose own `</div>` is indistinguishable
 * from any other. That container therefore opens a quote and never closes it —
 * which is where the quote runs to in real mail anyway, the end of the message.
 */
function applyQuoteMarkers(text: string): string {
  let out = '';
  let depth = 0;
  let atLineStart = true;

  for (const ch of text) {
    if (ch === QUOTE_OPEN) {
      depth++;
      continue;
    }
    if (ch === QUOTE_CLOSE) {
      depth = Math.max(0, depth - 1);
      continue;
    }
    if (ch === '\n') {
      out += ch;
      atLineStart = true;
      continue;
    }
    if (atLineStart) {
      if (ch === ' ' || ch === '\t') continue;
      if (depth > 0) out += '> ';
      atLineStart = false;
    }
    out += ch;
  }

  return out;
}

export function htmlToText(html: string | null | undefined): string | null {
  if (!html) return null;

  const stripped = html
    // Reserve the quote markers for our own use.
    .replace(/[\u0000\u0001]/g, '')
    .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1\s*>/gi, ' ')
    // Whitespace in the source is not content; the breaks added below are.
    .replace(/\s+/g, ' ')
    // Gmail and Apple Mail both quote with these, so both collapse on the `>`
    // markers below. Outlook does not — it emits a plain `divRplyFwdMsg` with a
    // From:/Sent: header block, so its quotes get no markers and split only
    // because `CUT_PATTERNS` matches that header block. Anyone retuning those
    // patterns is holding Outlook's only cut point.
    .replace(/<blockquote\b[^>]*>/gi, `\n\n${QUOTE_OPEN}`)
    .replace(/<\/blockquote\s*>/gi, `${QUOTE_CLOSE}\n\n`)
    .replace(/<div\b[^>]*class=["'][^"']*\bgmail_quote[^"']*["'][^>]*>/gi, `\n\n${QUOTE_OPEN}`)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<li\b[^>]*>/gi, '\n- ')
    // A row is a line; a cell is a word boundary. Minified table markup carries
    // no whitespace of its own, and "Ceremony florals$1,200" is a price line
    // rendered unreadable.
    .replace(/<\/(?:td|th)\s*>/gi, ' ')
    .replace(/<\/(?:p|div|tr|h[1-6])\s*>/gi, '\n\n')
    .replace(/<[^>]*>/g, '');

  // Entities are decoded only once no tag can be formed from the result, so an
  // `&lt;script&gt;` the vendor typed stays text instead of becoming markup.
  const text = applyQuoteMarkers(decodeEntities(stripped).replace(/[ \t]{2,}/g, ' '))
    .replace(/[ \t]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return text === '' ? null : text;
}
